/**
 * Goods-receipts list — KPI strip + status tabs + table + cursor "Load more".
 *
 * Faithful to `prototype/purchases/goods-receipts.html`:
 *   - page head : "Goods receipts" + lead line (N receipts in month · ₹ accepted
 *                 · shortage / awaiting) + "New goods receipt".
 *   - KPI strip : Received this month · Awaiting inspection (draft count) ·
 *                 Accepted value · Short / rejected (receipts-with-rejections).
 *   - tabs      : All · Draft · Posted · With shortage. "With shortage" is a
 *                 synthetic facet (posted rows with `rejectedLineCount > 0`),
 *                 filtered client-side since it isn't a wire enum value.
 *   - table     : GRN # · Against PO · Vendor · Received date · Items
 *                 (recv / ordered, shown as accepted-line ratio) · Status pill.
 *
 * @see prototype/purchases/goods-receipts.html — V1.1 baseline (list)
 * @see packages/api-contract/src/procedures/goods-receipts.ts
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownToLine, CircleCheck, CircleX, Clock, Plus, Search } from "lucide-react";
import { hasPermission } from "@flint/api-contract";
import { Alert, Button, StatusPill, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";
import { DataTableCard, ListPagination, avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.js";
import { meQueryOptions } from "../../../lib/session.js";
import { formatDate, paiseToInrShort } from "../../../lib/format.js";
import { grnRowPill, type GrnStatus } from "../lib/status.js";

type StatusFilter = "all" | "draft" | "posted" | "shortage";

const TABS: Array<{ id: StatusFilter; label: string; warn?: boolean }> = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "posted", label: "Posted" },
  { id: "shortage", label: "With shortage", warn: true },
];

export function GrnListPage() {
  const me = useQuery(meQueryOptions);
  const canManage = hasPermission(me.data?.company?.permissions ?? [], "goodsReceipts.manage");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [draftQuery, setDraftQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // The "shortage" facet isn't a wire status — pull unfiltered and slice
  // client-side. Draft / Posted map straight to the server `status` filter.
  const serverStatus: GrnStatus | undefined =
    statusFilter === "draft" ? "draft" : statusFilter === "posted" ? "posted" : undefined;

  // Debounce as-you-type — 300ms after the last keystroke the draft is
  // promoted to the committed query and hits the API. Enter still commits
  // immediately via submitSearch (skips the wait).
  useEffect(() => {
    const trimmed = draftQuery.trim();
    if (trimmed === committedQuery) return;
    const t = setTimeout(() => setCommittedQuery(trimmed), 300);
    return () => clearTimeout(t);
  }, [draftQuery, committedQuery]);

  // Cursor-stack pagination (the shared list pattern — see the PO list).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [statusFilter, committedQuery]);

  const list = trpc.goodsReceipts.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(serverStatus ? { status: serverStatus } : {}),
      ...(committedQuery ? { search: committedQuery } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCommittedQuery(draftQuery.trim());
  };

  const pageRows = list.data?.items ?? [];
  const rows = useMemo(
    () =>
      statusFilter === "shortage"
        ? pageRows.filter((r) => r.status === "posted" && r.rejectedLineCount > 0)
        : pageRows,
    [pageRows, statusFilter],
  );

  const counts = list.data?.counts;
  const kpis = list.data?.kpis;

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const totalRows =
    statusFilter === "shortage"
      ? kpis?.receiptsWithRejections
      : statusFilter === "draft"
        ? counts?.draft
        : statusFilter === "posted"
          ? counts?.posted
          : counts?.all;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const stats = computeGrnStats(rows);

  const lead = list.data
    ? `${kpis?.receiptsThisMonth ?? 0} receipts this month · ${paiseToInrShort(kpis?.acceptedValuePaise ?? "0")} accepted · ${kpis?.receiptsWithRejections ?? 0} with shortage`
    : null;

  // Tab counts: server counts for all/draft/posted; shortage is the
  // client-derived KPI count.
  const tabCount = (id: StatusFilter): number | undefined => {
    if (!counts) return undefined;
    if (id === "all") return counts.all;
    if (id === "draft") return counts.draft;
    if (id === "posted") return counts.posted;
    return kpis?.receiptsWithRejections;
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold tracking-tight text-ink">Goods receipts</h1>
          {lead ? <p className="mt-1 text-sm text-muted">{lead}</p> : null}
        </div>
        {canManage ? (
          <Button asChild>
            <Link to="/procurement/goods-receipts/new">
              <Plus size={15} />
              New goods receipt
            </Link>
          </Button>
        ) : null}
      </div>

      {list.error ? (
        <Alert intent="bad" title="Couldn't load goods receipts" description={list.error.message} />
      ) : null}

      {/* KPI strip */}
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          tone="sky"
          icon={<ArrowDownToLine size={16} />}
          label="Received this month"
          value={paiseToInrShort(kpis?.acceptedValuePaise ?? "0")}
          meta={`${kpis?.receiptsThisMonth ?? 0} receipts`}
        />
        <KpiTile
          tone="warn"
          icon={<Clock size={16} />}
          label="Awaiting inspection"
          value={String(counts?.draft ?? 0)}
          meta="draft receipts"
        />
        <KpiTile
          tone="ok"
          icon={<CircleCheck size={16} />}
          label="Accepted value"
          value={paiseToInrShort(kpis?.acceptedValuePaise ?? "0")}
          meta="this month"
        />
        <KpiTile
          tone="muted"
          icon={<CircleX size={16} />}
          label="With shortage"
          value={String(kpis?.receiptsWithRejections ?? 0)}
          meta="receipts with rejections"
        />
      </section>

      {/* Status tabs */}
      <TabsRoot value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
        <TabsList aria-label="Goods receipt status">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              count={tabCount(tab.id)}
              countTone={tab.warn ? "warn" : "default"}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      {/* Search toolbar */}
      <form onSubmit={submitSearch} className="flex items-center gap-2 flex-wrap">
        <label className="flex flex-1 max-w-sm items-center gap-2 h-9 rounded-md border border-line bg-paper px-3 text-muted focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
          <Search size={14} aria-hidden />
          <input
            ref={searchRef}
            type="search"
            placeholder="Search GRN #, vendor, PO…"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            aria-label="Search goods receipts"
            className="flex-1 min-w-0 border-0 bg-transparent text-sm text-ink placeholder:text-muted outline-none"
          />
        </label>
      </form>

      {/* Table */}
      <DataTableCard
        footer={
          rows.length > 0
            ? [
                { label: "Visible", value: rows.length },
                {
                  label: "Page accepted",
                  value: paiseToInrShort(stats.sumPaise.toString()),
                },
                { label: "Avg", value: paiseToInrShort(stats.avgPaise.toString()) },
                ...(stats.largest
                  ? [
                      {
                        label: "Largest",
                        value: paiseToInrShort(stats.largest.paise.toString()),
                        suffix: ` · ${stats.largest.vendor}`,
                      },
                    ]
                  : []),
                ...(stats.shortLines > 0
                  ? [
                      {
                        label: "Short lines",
                        value: stats.shortLines,
                        accent: "bad" as const,
                      },
                    ]
                  : []),
                ...(stats.dateRange ? [{ label: "Received", value: stats.dateRange }] : []),
              ]
            : []
        }
        pagination={
          rows.length > 0 ? (
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              {...(typeof totalRows === "number" ? { total: totalRows } : {})}
              sortLabel="sorted by received date (newest first)"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          ) : undefined
        }
      >
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">GRN #</th>
                <th className="px-3 py-2.5 text-left font-medium">Against PO</th>
                <th className="px-3 py-2.5 text-left font-medium">Vendor</th>
                <th className="px-3 py-2.5 text-left font-medium">Received date</th>
                <th className="px-3 py-2.5 text-right font-medium">Lines (acc / total)</th>
                <th className="px-3 py-2.5 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted">
                    Loading goods receipts…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-10 text-center text-sm text-muted">
                    {committedQuery ? (
                      <>
                        <p className="font-medium text-ink">No goods receipts match “{committedQuery}”.</p>
                        <p className="mt-1">Try a GRN number, PO number, or vendor name.</p>
                      </>
                    ) : statusFilter === "draft" ? (
                      <>
                        <p className="font-medium text-ink">No draft goods receipts.</p>
                        <p className="mt-1">Drafts appear here while you're still recording quantities.</p>
                      </>
                    ) : statusFilter === "posted" ? (
                      <>
                        <p className="font-medium text-ink">No posted goods receipts.</p>
                        <p className="mt-1">Posted receipts appear here after you commit a delivery.</p>
                      </>
                    ) : statusFilter === "shortage" ? (
                      <>
                        <p className="font-medium text-ink">No goods receipts with rejections.</p>
                        <p className="mt-1">Receipts appear here when any line has rejected units.</p>
                      </>
                    ) : (
                      <>
                        No goods receipts yet.
                        {canManage ? (
                          <>
                            {" "}
                            <Link to="/procurement/goods-receipts/new" className="text-sky-700 hover:underline">
                              Record your first receipt.
                            </Link>
                          </>
                        ) : null}
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const pill = grnRowPill(row.status, row.rejectedLineCount);
                  const acceptedLines = row.lineCount - row.rejectedLineCount;
                  return (
                    <tr
                      key={row.id}
                      className={[
                        "border-t border-line transition-colors hover:bg-raised",
                        row.status === "posted" && row.rejectedLineCount > 0 ? "bg-warn-50/40" : "",
                      ].join(" ")}
                    >
                      <td className="px-3 py-3 font-mono font-semibold text-ink-2">
                        {row.grnNumber ? (
                          <Link
                            to="/procurement/goods-receipts/$grnId"
                            params={{ grnId: row.id }}
                            className="hover:text-sky-700"
                          >
                            {row.grnNumber}
                          </Link>
                        ) : (
                          <Link
                            to="/procurement/goods-receipts/$grnId"
                            params={{ grnId: row.id }}
                            className="hover:underline"
                          >
                            <span className="text-muted">— draft —</span>
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {row.poNumber ? (
                          <Link
                            to="/procurement/purchase-orders/$poId"
                            params={{ poId: row.purchaseOrderId }}
                            className="font-mono font-semibold text-ink hover:text-sky-700"
                          >
                            {row.poNumber}
                          </Link>
                        ) : (
                          <span className="font-mono text-muted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <span
                            aria-hidden
                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.vendorName)}`}
                          >
                            {initialsFor(row.vendorName)}
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-ink truncate">{row.vendorName}</div>
                            {row.vendorCity ? (
                              <div className="text-xs text-muted">{row.vendorCity}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-ink-2">
                        {formatDate(row.receivedDate)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-ink-2">
                        {acceptedLines} / {row.lineCount}
                        {row.rejectedLineCount > 0 ? (
                          <div className="text-xs font-semibold text-amber-600">
                            {row.rejectedLineCount} short
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <StatusPill tone={pill.tone} size="sm" dot>
                          {pill.label}
                        </StatusPill>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
      </DataTableCard>
    </div>
  );
}

/** Visible-set aggregates for the stats footer (accepted value · short lines).
 *  Voided rows are excluded — their accepted value / short-line count was
 *  reversed at void time, so counting them here would contradict the KPI card
 *  above (which is server-computed and already excludes void). */
function computeGrnStats(
  rows: Array<{
    status: string;
    acceptedValuePaise: string;
    vendorName: string;
    receivedDate: string;
    rejectedLineCount: number;
  }>,
): {
  sumPaise: bigint;
  avgPaise: bigint;
  largest: { paise: bigint; vendor: string } | null;
  shortLines: number;
  dateRange: string | null;
} {
  const live = rows.filter((r) => r.status !== "void");
  if (live.length === 0) {
    return { sumPaise: 0n, avgPaise: 0n, largest: null, shortLines: 0, dateRange: null };
  }
  let sum = 0n;
  let largest: { paise: bigint; vendor: string } | null = null;
  let shortLines = 0;
  let min = live[0]!.receivedDate;
  let max = live[0]!.receivedDate;
  for (const r of live) {
    const v = BigInt(r.acceptedValuePaise);
    sum += v;
    shortLines += r.rejectedLineCount;
    if (largest === null || v > largest.paise) largest = { paise: v, vendor: r.vendorName };
    if (r.receivedDate < min) min = r.receivedDate;
    if (r.receivedDate > max) max = r.receivedDate;
  }
  return {
    sumPaise: sum,
    avgPaise: sum / BigInt(live.length),
    largest,
    shortLines,
    dateRange: min === max ? formatDate(min) : `${formatDate(min)} → ${formatDate(max)}`,
  };
}

function KpiTile({
  tone,
  icon,
  label,
  value,
  meta,
}: {
  tone: "sky" | "warn" | "ok" | "muted";
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  const stripe =
    tone === "sky"
      ? "bg-sky-600"
      : tone === "warn"
        ? "bg-warn"
        : tone === "ok"
          ? "bg-ok"
          : "bg-line-2";
  const iconWrap =
    tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "warn"
        ? "bg-warn-50 text-warn"
        : tone === "ok"
          ? "bg-ok-50 text-ok"
          : "bg-bg text-muted";
  return (
    <div className="relative overflow-hidden rounded-md border border-line bg-paper p-4 shadow-sm">
      <span className={`absolute inset-y-0 left-0 w-1 ${stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted">{label}</span>
        <span className={`grid h-7 w-7 place-items-center rounded-md ${iconWrap}`}>{icon}</span>
      </div>
      <div className="mt-1.5 text-xl font-bold tracking-tight text-ink">{value}</div>
      <div className="mt-0.5 text-xs text-muted">{meta}</div>
    </div>
  );
}
